/*
 * THE DEMO'S PAGE SLOTS: the state behind Select / Remove and drag-and-drop reordering, so those controls WORK in the demo instead of doing nothing. Same rules as the real server
 * (skydive_backend SiteApi reorderMoves): a move names the page and the slot it believes it is leaving; if that no longer matches, it is a conflict and the caller gets the true state back.
 */
import { PAGE_SLOT_COUNT } from "../util/slotCount";
export interface DemoPage { _id: string; name: string; selected: boolean; slotIndex: number | null; restricted: boolean; firstVisitDate?: string }
export const SLOT_COUNT = PAGE_SLOT_COUNT;

export interface SlotMove { pageName: string; fromSlot: number; toSlot: number }

export const createPageStore = (initial: DemoPage[]) => {
  const pages: DemoPage[] = initial.map((p) => ({ ...p }));
  const snapshot = () => pages.map((p) => ({ ...p }));
  const freeSlot = () => { for (let i = 0; i < SLOT_COUNT; i++) if (!pages.some((p) => p.selected && p.slotIndex === i)) return i; return null; };

  return {
    pages: snapshot,
    /** a replay clip's own pages: this is the whole list now (the recipient's moves and removals edit it; opening the clip again starts from the clip's) */
    replaceAll(next: DemoPage[]) {
      pages.splice(0, pages.length, ...next.map((p) => ({ ...p })));

      return { pages: snapshot() };
    },
    /** take pages out of the list (a group has taken them over), freeing any slot they held */
    removeByName(names: string[]) {
      for (const name of names) {
        const i = pages.findIndex((p) => p.name === name);
        if (i >= 0) pages.splice(i, 1);
      }

      return { pages: snapshot() };
    },
    /** the entry called `from` is called `to`, keeping its slot (a group was renamed); nothing happens when there is none or `to` is taken */
    rename(from: string, to: string) {
      const p = pages.find((x) => x.name === from);
      if (p && !pages.some((x) => x.name === to)) { p.name = to; p._id = `p-${to}`; }

      return { pages: snapshot() };
    },
    /** a page the site has now seen, not in any slot (the real server adds every new URL like this); a page already there is left alone */
    addUnselected(name: string, firstVisitDate?: string) {
      if (!pages.some((p) => p.name === name)) pages.push({ _id: `p-${name}`, name, selected: false, slotIndex: null, restricted: false, ...(firstVisitDate ? { firstVisitDate } : {}) });

      return { pages: snapshot() };
    },
    select(pageName: string, slotIndex?: number | null) {
      const taken = slotIndex !== undefined && slotIndex !== null && pages.some((p) => p.selected && p.slotIndex === slotIndex && p.name !== pageName);
      const slot = slotIndex !== undefined && slotIndex !== null && !taken ? slotIndex : freeSlot();
      if (slot === null) return { pages: snapshot(), error: "no free slot" };
      const existing = pages.find((p) => p.name === pageName);
      if (existing) { existing.selected = true; existing.slotIndex = slot; } else pages.push({ _id: `p-${pageName}`, name: pageName, selected: true, slotIndex: slot, restricted: false });

      return { pages: snapshot() };
    },
    unselect(pageName: string) {
      const p = pages.find((x) => x.name === pageName);
      if (p) { p.selected = false; p.slotIndex = null; }

      return { pages: snapshot() };
    },
    reorder(moves: SlotMove[]) {
      if (!Array.isArray(moves) || moves.length === 0) return { status: "noop", pages: snapshot() };
      const byName = new Map(pages.filter((p) => p.selected).map((p) => [p.name, p]));
      for (const m of moves) { const p = byName.get(m.pageName); if (!p || p.slotIndex !== m.fromSlot) return { status: "conflict", pages: snapshot() }; }       // the caller's picture is out of date
      const after = new Map<string, number>(pages.filter((p) => p.selected).map((p) => [p.name, p.slotIndex as number]));
      moves.forEach((m) => after.set(m.pageName, m.toSlot));
      const slots = Array.from(after.values());
      if (new Set(slots).size !== slots.length || slots.some((s) => s < 0 || s >= SLOT_COUNT)) return { status: "conflict", pages: snapshot() };   // two pages in one slot, or off the grid
      pages.forEach((p) => { if (p.selected) p.slotIndex = after.get(p.name) as number; });

      return { status: "ok", pages: snapshot() };
    },
  };
};
export type PageStore = ReturnType<typeof createPageStore>;
